/// <amd-module name="TFS/DRITools/Components/UsageInsights/Visualizations" />
import d3 = require("d3");
import * as React from "react";

import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

// DRI Tools
import { parseDate } from "TFS/DRITools/util/Date";
import { DataInfo, Line, OnePoint } from "TFS/DRITools/util/RawDataTypes";
import { Obj } from "TFS/DRITools/util/RequestNode";

// Resources
import * as Resources from "TFS/DRITools/Resources";

export interface IVisualizationProps extends IVssComponentProperties {
    data: Line[];
    width: number;
    height: number;
    graphInfo: DataInfo;
    chosenLines: string[];
}

export interface IVisualizationState extends IVssComponentProperties {
    zoomTransform: any;
}

export interface IColumnVsColumnProps extends IVssComponentProperties {
    data: Line[];
    width: number;
    height: number;
    zoomTransform: any;
    graphInfo: DataInfo;
    chosenLines: string[];
}

interface axisOffset {
    shiftRight: number;
    heightOffset: number;
}

export class Visualizations extends VssComponent<IVisualizationProps, IVisualizationState> {
    private myRef: SVGGElement | null;
    private _zoom: d3.ZoomBehavior<SVGGElement, {}>;

    public constructor(props: IVisualizationProps, context: IVssComponentContext) {
        super(props, context);
        const { height, width } = props;
        this.state = {
            zoomTransform: undefined
        };

        this._zoom = d3
            .zoom<SVGGElement, {}>()
            .scaleExtent([1, 55])
            .translateExtent([[0, 0], [width, height]])
            .extent([[0, 0], [width, height]])
            .on("zoom", this._zoomed);
    }

    public componentDidMount() {
        d3.select(this.myRef).call(this._zoom);
    }

    public componentDidUpdate() {
        d3.select(this.myRef).call(this._zoom);
    }

    public render() {
        const { zoomTransform } = this.state;
        const { width, height, graphInfo, chosenLines } = this.props;
        return (
            <div className="visualization-wrapper">
                <div className="tooltip" id={graphInfo.name.split(" ")[0]}>
                    <strong>{graphInfo.name}</strong>
                </div>
                <svg ref={ref => (this.myRef = ref)} width={width} height={height}>
                    <ColumnVsColumn
                        data={this.props.data}
                        width={width}
                        height={height}
                        zoomTransform={zoomTransform}
                        graphInfo={graphInfo}
                        chosenLines={chosenLines}
                    />
                </svg>
            </div>
        );
    }

    private _zoomed = () => {
        this.setState({
            zoomTransform: d3.event.transform
        });
    };
}

class ColumnVsColumn extends VssComponent<IColumnVsColumnProps, {}> {
    private myRef: SVGGElement | null;
    private _xScale: d3.ScaleTime<number, number>;
    private _yScale: d3.ScaleLinear<number, number>;
    // Points cache because people usually either don't hover over it at all or do it a lot.
    private _cachePoints: Obj = {};

    public componentDidMount() {
        this._updateD3(this.props);
    }

    public componentDidUpdate() {
        this._updateD3(this.props);
    }

    public render(): JSX.Element {
        return <g ref={ref => (this.myRef = ref)} style={{ position: "relative" }} />;
    }

    private _updateD3(props: IColumnVsColumnProps) {
        const { zoomTransform, data, graphInfo, chosenLines } = props;
        let { width, height } = props;

        const wrapper = d3.select(this.myRef);
        wrapper.selectAll("*").remove();

        const margin = { top: 0, right: 50, bottom: 110, left: 50 };

        width = width - margin.left - margin.right;
        height = height - margin.bottom;

        this._xScale = d3
            .scaleTime()
            .domain([data[0].values[0].x, data[0].values[data[0].values.length - 1].x])
            .range([0, width]);
        this._yScale = d3
            .scaleLinear()
            .domain([0, graphInfo.maxY])
            .range([height, 0]);

        const canvas = wrapper
            .append("g")
            .attr("class", "canvas")
            .attr("transform", "translate(" + margin.left + "," + 5 + ") ");

        if (zoomTransform && zoomTransform.k >= 1) {
            this._xScale.domain(zoomTransform.rescaleX(this._xScale).domain());
        }

        // @ts-ignore TypeScript 3.5 update (maybe need to update d3 typing?)
        const axisBox: axisOffset = this._drawXYaxis(canvas, height);

        const tooltip = d3
            .select("div.tooltip#" + graphInfo.name.split(" ")[0])
            .style("display", "none")
            .style("height", chosenLines.length * 28 + 56 + "px");

        if (chosenLines.length === 0) {
            return;
        }

        const chartline = d3
            .line<OnePoint>()
            .x((d: OnePoint) => {
                return this._xScale(d.x);
            })
            .y((d: OnePoint) => {
                return this._yScale(d.y);
            })
            .curve(d3.curveMonotoneX);

        // Hover focus, contains x-hover-line, line circles
        const gHover = wrapper
            .append("g")
            .attr("class", "focus")
            .style("display", "none")
            .attr("transform", "translate(" + margin.left + "," + 10 + ") ");

        const linesCanvas = canvas
            .append("svg")
            .style("height", height + 0 + margin.bottom - axisBox.heightOffset)
            .attr("transform", "translate(" + axisBox.shiftRight + "," + 0 + ") ")
            .append("g");

        data.forEach(line => {
            if ((this.props.chosenLines as any).includes(line.key)) {
                if (line.key !== Resources.UsageInsights_NumBlockedRequests && line.key !== Resources.UsageInsights_NumDelayedRequests) {
                    // @ts-ignore TypeScript 3.5 update
                    this._drawLine(line, linesCanvas, gHover, chartline);
                } else {
                    // @ts-ignore TypeScript 3.5 update
                    this._blockPoints(line, linesCanvas);
                }
            }
        });

        // X axis line on hover
        gHover
            .append("line")
            .attr("class", "x-hover-line hover-line")
            .attr("y1", 0)
            .attr("y2", height);

        const bisectDate = d3.bisector((d: OnePoint) => {
            return d.x;
        }).left;

        const overlay: d3.Selection<d3.BaseType, unknown, null, undefined> = wrapper
            .append("rect")
            .attr("transform", "translate(" + margin.left + "," + 10 + ")")
            .attr("class", "overlay")
            .attr("width", width)
            .attr("height", height);

        overlay
            .on("mouseover", () => {
                gHover.style("display", null);
                tooltip.style("display", null);
            })
            .on("mouseout", () => {
                gHover.style("display", "none");
                tooltip.style("display", "none");
            })
            .on("mousemove", () => {
                if (data.length !== 0) {
                    tooltip.selectAll("p").remove();

                    // Needed to find the closest date
                    const xMouse: Date = this._xScale.invert(d3.mouse((overlay as any).node())[0]),
                        iOfMouse = bisectDate(data[0].values, xMouse, 1),
                        d0: OnePoint = data[0].values[iOfMouse - 1 >= 0 ? iOfMouse - 1 : 0],
                        d1: OnePoint = data[0].values[iOfMouse < data[0].values.length ? iOfMouse : data[0].values.length - 1],
                        closestPoint: OnePoint = xMouse.getTime() - d0.x.getTime() > d1.x.getTime() - xMouse.getTime() ? d1 : d0;

                    if (this._xScale(closestPoint.x) > width || this._xScale(closestPoint.x) < 0) {
                        return;
                    }

                    tooltip
                        .append("p")
                        .attr("class", "tooltip-datetime")
                        .text(parseDate(closestPoint.x) + ":");

                    if (!this._cachePoints[closestPoint.x.toString()]) {
                        const savedPoints: { point: OnePoint; line: Line }[] = [];
                        data.forEach(line => {
                            if ((chosenLines as any).includes(line.key)) {
                                const i = bisectDate(line.values, xMouse, 1),
                                    d0: OnePoint = line.values[i - 1 >= 0 ? i - 1 : 0],
                                    d1: OnePoint = line.values[i < line.values.length ? i : line.values.length - 1],
                                    d = xMouse.getTime() - d0.x.getTime() > d1.x.getTime() - xMouse.getTime() ? d1 : d0;
                                savedPoints.push({ point: d, line: line });
                            }
                        });
                        savedPoints.sort(
                            (a: { point: OnePoint; line: Line }, b: { point: OnePoint; line: Line }): number => {
                                return b.point.y - a.point.y;
                            }
                        );
                        this._cachePoints[closestPoint.x.toString()] = savedPoints;
                    }
                    this._cachePoints[closestPoint.x.toString()].forEach((d: { point: OnePoint; line: Line }) => {
                        if ((chosenLines as any).includes(d.line.key)) {
                            this._onHover(d.point, d.line, gHover, tooltip);
                        }
                    });
                }
            });
    }

    private _drawXYaxis(canvas: d3.Selection<d3.BaseType, {}, null, undefined>, height: number): axisOffset {
        const x = canvas
            .append("g")
            .attr("class", "x axis")
            .attr("transform", "translate(0," + height + ")")
            .call(d3.axisBottom(this._xScale).tickFormat(d3.timeFormat("%m/%d/%Y %H:%M:%S")));

        const y = canvas
            .append("g")
            .attr("class", "y axis")
            .call(d3.axisLeft(this._yScale));

        return {
            shiftRight: (y.node() as SVGAElement).getBBox().width || 0,
            heightOffset: (x.node() as SVGAElement).getBBox().height || 0
        };
    }

    private _blockPoints(line: Line, canvas: d3.Selection<d3.BaseType, {}, null, undefined>) {
        line.values.forEach((point: OnePoint) => {
            if (point.y === 1) {
                canvas
                    .append("circle")
                    .attr("cx", this._xScale(point.x))
                    .attr("cy", 4)
                    .attr("r", 3)
                    .attr("stroke", line.color ? line.color : 0)
                    .attr("fill", line.color ? line.color : 0);
            }
        });
    }

    private _drawLine(
        line: Line,
        canvas: d3.Selection<d3.BaseType, {}, null, undefined>,
        gHover: d3.Selection<d3.BaseType, {}, null, undefined>,
        chartline: any
    ) {
        const newLine = chartline(line.values);
        canvas
            .append("path")
            .attr("class", "line")
            .style("stroke", line.color ? line.color : "black")
            .attr("id", "tag" + line.key.replace(/ /g, "_"))
            .attr("d", newLine ? newLine.toString() : "");
        gHover
            .append("circle")
            .attr("r", 5)
            .attr("stroke", line.color ? line.color : 0)
            .attr("id", "tag" + line.key.replace(/ /g, "_"));
    }

    private _onHover(
        point: OnePoint,
        line: Line,
        gHover: d3.Selection<d3.BaseType, unknown, null, undefined>,
        tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>
    ) {
        gHover
            .select("circle#tag" + line.key.replace(/ /g, "_"))
            .style("opacity", point.y !== 0 ? 1 : 0)
            .attr("transform", "translate(" + this._xScale(point.x) + "," + this._yScale(point.y) + ")");
        gHover.select(".x-hover-line").attr("transform", "translate(" + this._xScale(point.x) + "," + 0 + ")");

        let value: string | number = point.y;
        if (value.toFixed(5).length < value.toString().length) {
            value = parseFloat(value.toFixed(5)) === 0 ? value.toExponential() : value.toFixed(5);
        }

        const lineDescription = tooltip.style("left", this._xScale(point.x) + "px").append("p");
        lineDescription
            .append("span")
            .attr("class", "dot")
            .style("background-color", line.color ? line.color : "black");
        lineDescription.append("span").text(line.key + " = ");
        lineDescription
            .append("strong")
            .text(
                line.key === Resources.UsageInsights_NumDelayedRequests || line.key === Resources.UsageInsights_NumBlockedRequests
                    ? value === 1
                        ? "true"
                        : false
                    : value
            );
    }
}
